import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CAD, statusTone } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import {
  Users, FileText, Send, ScrollText, Clock, CheckCircle2, CalendarDays, PartyPopper,
  XCircle, DollarSign, TrendingUp, Wallet, Plus, ArrowRight,
} from "lucide-react";

const CARD_DEFS = [
  { key: "new_leads", label: "New Leads", icon: Users, money: false },
  { key: "quotes_pending", label: "Quotes Pending", icon: FileText, money: false },
  { key: "quotes_sent", label: "Quotes Sent", icon: Send, money: false },
  { key: "contracts_pending", label: "Contracts Pending", icon: ScrollText, money: false },
  { key: "deposits_pending", label: "Deposits Pending", icon: Clock, money: false },
  { key: "deposits_paid", label: "Deposits Paid", icon: CheckCircle2, money: false },
  { key: "upcoming_events", label: "Upcoming Events", icon: CalendarDays, money: false },
  { key: "completed_events", label: "Completed", icon: PartyPopper, money: false },
  { key: "cancelled_events", label: "Cancelled", icon: XCircle, money: false },
  { key: "monthly_revenue", label: "Monthly Revenue", icon: DollarSign, money: true },
  { key: "total_booked_value", label: "Total Booked", icon: TrendingUp, money: true },
  { key: "outstanding_balances", label: "Outstanding", icon: Wallet, money: true },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);

  const cards = data?.cards || {};
  const upcoming = data?.upcoming_events_list || [];

  return (
    <div className="animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="eyebrow mb-2">Overview</p>
          <h1 className="font-serif text-4xl">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/admin/leads?new=1")} className="btn-dark" data-testid="quick-new-lead"><Plus className="h-4 w-4" /> New Lead</button>
          <button onClick={() => navigate("/admin/events")} className="btn-outline" data-testid="quick-events">View Events</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
        {CARD_DEFS.map(({ key, label, icon: Icon, money }) => (
          <div key={key} className="df-card p-5" data-testid={`stat-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
              <Icon className="h-4 w-4 text-olive" />
            </div>
            <div className="font-serif text-3xl">{money ? CAD(cards[key]) : (cards[key] ?? 0)}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 df-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">Upcoming events</h2>
            <button onClick={() => navigate("/admin/calendar")} className="text-sm text-olive flex items-center gap-1 hover:gap-2 transition-all">Calendar <ArrowRight className="h-3 w-3" /></button>
          </div>
          {upcoming.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No upcoming events.</p>}
          <div className="space-y-2">
            {upcoming.map((e) => (
              <button key={e.id} onClick={() => navigate(`/admin/events/${e.id}`)} className="w-full text-left flex items-center justify-between gap-4 p-4 rounded-xl hover:bg-clay transition-colors" data-testid={`dash-event-${e.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{e.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{e.event_type} · {e.event_city}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm text-muted-foreground">{e.event_date}</span>
                  {e.menu_selection_status && e.menu_selection_status !== "Not sent" && <Badge variant="outline" className={statusTone(e.menu_selection_status)} data-testid={`dash-menu-status-${e.id}`}>{e.menu_selection_status}</Badge>}
                  <Badge variant="outline" className={statusTone(e.event_status)}>{e.event_status}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="df-card p-6">
          <h2 className="font-serif text-2xl mb-5">Snapshot</h2>
          <div className="space-y-4 text-sm">
            <Row label="Total leads" value={data?.total_leads ?? 0} />
            <Row label="Total events" value={data?.total_events ?? 0} />
            <Row label="Conversion rate" value={`${data?.conversion_rate ?? 0}%`} />
            <Row label="Avg quote value" value={CAD(data?.avg_quote_value)} />
            <Row label="Upcoming revenue" value={CAD(data?.upcoming_revenue)} />
            <Row label="Deposits collected" value={CAD(data?.deposit_totals)} />
          </div>
          <div className="mt-6 pt-6 border-t border-line">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Most requested</p>
            <div className="flex flex-wrap gap-2">
              {(data?.most_requested_cocktails || []).slice(0, 6).map((c) => (
                <span key={c.name} className="text-xs px-3 py-1.5 rounded-full bg-clay">{c.name} · {c.count}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);
